import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/pages/Login";
import GoogleSignInButton from "@/components/GoogleSignInButton";

function formatDetail(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).join(" ").trim() || "Invalid input";
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created — welcome!");
      navigate("/app");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Free forever — 5 rewrites every day."
      footer={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-indigo-700 hover:underline"
            data-testid="go-to-login"
          >
            Sign in
          </Link>
        </>
      }
    >
      <div className="space-y-4">
        <GoogleSignInButton
          label="Sign up with Google"
          testId="google-sign-in-button-register"
        />
        <div className="relative py-1 text-center">
          <span className="relative z-10 bg-white px-3 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            or with email
          </span>
          <span className="absolute inset-x-0 top-1/2 h-px bg-slate-200" />
        </div>
      </div>
      <form onSubmit={submit} className="mt-4 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="name">Your name</Label>
          <Input
            id="name"
            data-testid="register-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Alex"
            className="rounded-xl border-[#E6E2D8] bg-white focus-visible:ring-indigo-500/40"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="register-email-input"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="rounded-xl border-[#E6E2D8] bg-white focus-visible:ring-indigo-500/40"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            data-testid="register-password-input"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            className="rounded-xl border-[#E6E2D8] bg-white focus-visible:ring-indigo-500/40"
          />
        </div>
        <Button
          type="submit"
          data-testid="register-submit-button"
          disabled={loading}
          className="group h-11 w-full gap-2 rounded-full bg-[#1A1D20] text-sm font-semibold text-[#FAF9F5] hover:bg-[#2A2E3D]"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              Create free account
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </>
          )}
        </Button>
      </form>
    </AuthShell>
  );
}
